import { useState, useEffect, useRef, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { API, Auth } from 'aws-amplify';

// Context and utilities
import { useAppContext } from '../../Lib/contextLib';
import { getVideo, GetUserVideo } from '../../s3Func/s3Download';

// Components
import PopUp from '../../Popup/logoutPopup';
import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat';
import Learn from '../../Learn/Learn';
import Practice from '../Module13/Practice';
import SingingTime from '../../SingingTime/SingingTime';
import Celebrate from '../../Celebrate/Celebrate';
import Surprise from '../../SurpriseVideo/surprise';
import FollowTheBeatModule8 from '../../FollowTheBeat/FollowTheBeatModule8.js';

// Assets
import presentSound from '../../Unit1/Module1/assets/ta_da_gift.mp3';
import loadingGif from '../../loading-gif-png-5.gif';

// Styles
import './Module13World.css';
import '../../Animations.css';

// ============================================================================
// Constants
// ============================================================================

const AFTER_CELEBRATE_VIDEO_DAY9 = 'appfiles/change_feelings/closing.mp4';
const MODULE_NAME = 'MODULE13';
const NAVIGATION_PATH = '/changefeelings/navigation';
const SPECIAL_DAYS = { DAY_4: 4, DAY_8: 8 };
const COIN_PRIZE_THRESHOLD = 100;

// Step completion states
const STEPS = {
  NOT_STARTED: 'notstarted',
  LEARN: 'learn',
  PRACTICE: 'practice',
  FOLLOW_THE_BEAT: 'followthebeat',
  CELEBRATE: 'celebrate',
  SINGING_TIME: 'singingtime',
  SURPRISE: 'surprise',
};

// Grade types that bypass certain restrictions
const UNRESTRICTED_GRADES = ['Teacher Mode', 'Free Play'];

// ============================================================================
// Utility Functions
// ============================================================================

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOSDevice = () => {
  const iosDevices = [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
  ];
  return (
    iosDevices.includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

const isUnrestrictedGrade = (grade) => UNRESTRICTED_GRADES.includes(grade);

// ============================================================================
// Custom Hooks
// ============================================================================

const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

// ============================================================================
// Main Component
// ============================================================================

const Module13World = () => {
  const { width: clientWidth, height: clientHeight } = useWindowDimensions();
  const { userHasAuthenticated } = useAppContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { module13 } = useSelector((state) => state.module13, shallowEqual);
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);

  // Refs for tracking initial renders
  const firstUpdate = useRef(true);
  const coinUpdate = useRef(true);

  // ------------------------------------------------------------------------
  // Image URL Generator
  // ------------------------------------------------------------------------
  const generateImageUrl = (baseUrl) => login.cloudFront + baseUrl + login.cookie;

  // ------------------------------------------------------------------------
  // Image Assets
  // ------------------------------------------------------------------------
  const images = {
    // Background and character
    background: generateImageUrl('images/Module8Images/Module8NavImages/Background_JourneyMap_Unit2_Mod3.png'),
    animal: generateImageUrl('images/Module3Images/Lizard+closing+still+image.gif'),
    fly: generateImageUrl('images/Module3Images/Fly.png'),

    // UI Elements
    coinPick: generateImageUrl('images/Images/AwesomeCoin.png'),
    coinBar: generateImageUrl('images/Images/Coin+Counter.png'),
    journeyMap: generateImageUrl('images/Module1Images/bluejourneymap.svg'),
    greenCheck: generateImageUrl('images/Module1Images/GreenCheck-Button.png'),
    present: generateImageUrl('images/Images/Gift.png'),

    // Buttons
    logoutBtn: generateImageUrl('images/Module1Images/Logout.png'),
    reviewBtn: generateImageUrl('images/Module1Images/Button_Review.png'),
    rewardBtn: generateImageUrl('images/Module1Images/Button_Reward_3x.png'),
    celebrateBtn: generateImageUrl('images/Module1Images/Button_Celebrate.png'),
    learnBtn: generateImageUrl('images/Module1Images/Button_Learn.png'),
    practiceBtn: generateImageUrl('images/Module1Images/Button_Practice.png'),
    ftbBtn: generateImageUrl('images/Images/Button_FTB.png'),
    singingTimeBtn: generateImageUrl('images/Module1Images/Button_SingingTime.png'),

    // Progress bars (lizard tongue)
    progressBars: [
      generateImageUrl('images/Module3Images/LizTongue_Empty_100819.svg'),
      generateImageUrl('images/Module3Images/LizTongue_1out3_100819.svg'),
      generateImageUrl('images/Module3Images/LizTongue_2out3_100819.svg'),
      generateImageUrl('images/Module3Images/LizTongue_2out3_100819.svg'),
      generateImageUrl('images/Module3Images/LizTongue_Complete_100819.svg'),
    ],

    // Rewards
    rewards: {
      CactusBarell: generateImageUrl('images/Module3Images/Reward_SmallCactus1.png'),
      CactusPrickly: generateImageUrl('images/Module3Images/Reward_SmallCactus2.png'),
      StegoBlue: generateImageUrl('images/Module8Images/DailyReward_Stego_Blue.png'),
      StegoGreen: generateImageUrl('images/Module8Images/DailyReward_Stego_Green.png'),
      Tent: generateImageUrl('images/Module3Images/Reward_Tent1.png'),
      TentOld: generateImageUrl('images/Module3Images/Reward_Tent2.png'),
      LongNeckGreen: generateImageUrl('images/Module8Images/DailyReward_DinoLongNeck_Green.png'),
      LongNeckRed: generateImageUrl('images/Module8Images/DailyReward_DinoLongNeck_Red.png'),
      Backpack: generateImageUrl('images/Module3Images/Reward_Backpack.png'),
      Canteen: generateImageUrl('images/Module3Images/Reward_Cantina.png'),
      Plant1: generateImageUrl('images/Module8Images/DailyReward_Plant1.png'),
      Plant2: generateImageUrl('images/Module8Images/DailyReward_Plant2.png'),
      Hat: generateImageUrl('images/Module3Images/Reward_Cowboyhat.png'),
      Bandanna: generateImageUrl('images/Module3Images/Reward_Bandana.png'),
      RaptorLight: generateImageUrl('images/Module8Images/DailyReward_Raptor_LightBlue.png'),
      RaptorDark: generateImageUrl('images/Module8Images/DailyReward_Raptor_Darker.png'),
      Volcano: generateImageUrl('images/newDesign/Unit2Module8/BigRewardVolcanoNoSmoke.png'),
      TRex: generateImageUrl('images/Module8Images/BigReward_TRex@2x.png'),
    },
  };

  // Progress bar arrays for different day types
  const progressBar5Keys = [
    images.progressBars[0],
    images.progressBars[1],
    images.progressBars[2],
    images.progressBars[2],
    images.progressBars[3],
    images.progressBars[4],
  ];
  const progressBar4Keys = images.progressBars;

  // Reward options by day
  const rewardOptionsByDay = {
    1: [images.rewards.CactusBarell, images.rewards.CactusPrickly],
    2: [images.rewards.StegoBlue, images.rewards.StegoGreen],
    3: [images.rewards.Tent, images.rewards.TentOld],
    4: [images.rewards.LongNeckRed, images.rewards.LongNeckGreen],
    5: [images.rewards.Canteen, images.rewards.Backpack],
    6: [images.rewards.Plant1, images.rewards.Plant2],
    7: [images.rewards.Bandanna, images.rewards.Hat],
    8: [images.rewards.RaptorDark, images.rewards.RaptorLight],
  };

  // Influencers array
  const influencers = [
    login.influencerid,
    login.influencerid1,
    login.influencerid2,
    login.influencerid3,
    login.influencerid4,
  ];

  // Audio for touch interactions
  const touchSound = new Audio(generateImageUrl('media/Module1Videos/touch_button.mp3'));

  // ------------------------------------------------------------------------
  // UI State
  // ------------------------------------------------------------------------
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Icon visibility state
  const [reviewIconVisible, setReviewIconVisible] = useState(false);
  const [keyIcon, setKeyIcon] = useState(false);
  const [coinIcon, setCoinIcon] = useState(false);
  const [presentIcon, setPresentIcon] = useState(false);

  // Reward selection state
  const [rewardOption1, setRewardOption1] = useState(null);
  const [rewardOption2, setRewardOption2] = useState(null);
  const [displayReward, setDisplayReward] = useState(false);
  const [displayGreenCheck, setDisplayGreenCheck] = useState(false);
  const [dropZone, setDropZone] = useState(false);
  const [displayGreenButton, setDisplayGreenButton] = useState(false);

  // Screen/component visibility state
  const [displayLearn, setDisplayLearn] = useState(false);
  const [displayPractice, setDisplayPractice] = useState(false);
  const [displayDay4Practice, setDisplayDay4Practice] = useState(false);
  const [displaySingingTime, setDisplaySingingTime] = useState(false);
  const [displayCelebrate, setDisplayCelebrate] = useState(false);
  const [displayBody, setDisplayBody] = useState(true);
  const [displayIntroVideo, setDisplayIntroVideo] = useState(false);
  const [displaySurprise, setDisplaySurprise] = useState(false);
  const [displayVoice, setDisplayVoice] = useState(false);
  const [displayFTB, setDisplayFTB] = useState(false);

  // Component content state
  const [learnComponent, setLearnComponent] = useState('');
  const [day4PracticeComponent, setDay4PracticeComponent] = useState('');
  const [singTimeComponent, setSingTimeComponent] = useState('');
  const [celebrateComponent, setCelebrateComponent] = useState('');
  const [introVideo, setIntroVideo] = useState('');
  const [surpriseVideo, setSurpriseVideo] = useState('');

  // Animal/Lizard state
  const [showAnimal, setShowAnimal] = useState(false);
  const [animalState, setAnimalState] = useState('');
  const [animalVoice, setAnimalVoice] = useState('');
  const [playLizardClose, setPlayLizardClose] = useState(false);
  const [showFly, setShowFly] = useState(false);

  // Prize state
  const [prize, setPrize] = useState(false);
  const [displayPrize, setDisplayPrize] = useState(false);

  // Animation state
  const [rewardAnimationState, setRewardAnimationState] = useState('');
  const [coinAnimationState, setCoinAnimationState] = useState('bounceIcons');

  // Audio state
  const [touchButton, setTouchButton] = useState(false);
  const [checkSound, setCheckSound] = useState(false);
  const [buttonSound, setButtonSound] = useState(false);
  const [rewardsFound, setRewardsFound] = useState(false);
  const [coinAudio, setCoinAudio] = useState(false);
  const [audioReady, setAudioReady] = useState(false);

  // Tracking state
  const [checkStep, setCheckStep] = useState('');
  const [showReward, setShowReward] = useState(false);

  // Follow The Beat (FTB) assets
  const [ftbMusic, setFTBMusic] = useState('');
  const [ftbInstruction, setFTBInstruction] = useState('');
  const [ftbInstructionGIF, setFTBInstructionGIF] = useState('');
  const [ftbCount, setFTBCount] = useState('');
  const [ftbTutorial1, setFTBTutorial1] = useState('');
  const [ftbTutorial2, setFTBTutorial2] = useState('');
  const [ftbWalkthrough, setFTBWalkthrough] = useState('');

  // GIF and audio assets
  const [specialMessage, setSpecialMessage] = useState('');
  const [watchConversation, setWatchConversation] = useState('');
  const [amazingJob, setAmazingJob] = useState('');
  const [awesomeJob, setAwesomeJob] = useState('');
  const [practiceIntro, setPracticeIntro] = useState('');
  const [learnIntro, setLearnIntro] = useState('');
  const [ftbIntro, setFtbIntro] = useState('');
  const [celebrateIntro, setCelebrateIntro] = useState('');
  const [lizardTongue, setLizardTongue] = useState('');
  const [lizardTongueAudio, setLizardTongueAudio] = useState('');
  const [animalClose, setAnimalClose] = useState('');
  const [animalCloseAudio, setAnimalCloseAudio] = useState('');

  // ------------------------------------------------------------------------
  // Audio Helper
  // ------------------------------------------------------------------------

  const playButtonAudio = useCallback(() => {
    sleep(700).then(() => {
      setButtonSound(true);
    });
  }, []);

  // ------------------------------------------------------------------------
  // Asset Loading
  // ------------------------------------------------------------------------

  const loadGifAssets = useCallback(() => {
    // Common GIF assets
    setAwesomeJob(getVideo('appfiles/ask/ask_awesome_job.mp3', login));
    setLizardTongue(getVideo('appfiles/expressions/lizard_fly.gif', login));
    setSpecialMessage(getVideo('appfiles/ask/ask_special_message.gif', login));
    setWatchConversation(getVideo('appfiles/ask/ask_conversation.gif', login));
    setAmazingJob(getVideo('appfiles/ask/Ask Awesome Job.gif', login));
    setPracticeIntro(getVideo('appfiles/ask/ask_practice_intro.gif', login));
    setLearnIntro(getVideo('appfiles/ask/ask_learn_intro.gif', login));
    setFtbIntro(getVideo('appfiles/ask/ask_ftb_intro.gif', login));
    setCelebrateIntro(getVideo('appfiles/ask/ask_celebrate_intro.gif', login));
    setAnimalClose(getVideo('appfiles/ask/ask_closing.gif', login));
    setFTBInstructionGIF(getVideo('appfiles/ask/ask_lizard_ftb_instructions.gif', login));
    setFTBCount(getVideo('appfiles/ask/countingLizard.gif', login));

    // Audio assets
    setLizardTongueAudio(getVideo('appfiles/expressions/expressions_fly_catch.mp3', login));
    setAnimalCloseAudio(getVideo('appfiles/change_feelings/change_feelings_day_finished.mp3', login));
    setFTBInstruction(getVideo('appfiles/change_feelings/change_feelings_ftb_instructions.mp3', login));
    setFTBMusic(getVideo('appfiles/ask/ask_ftb_audio.m4a', login));

    // Tutorial assets (only for first-time users)
    if (!module13.unlockedDays.day2) {
      setFTBTutorial1(getVideo('appfiles/greet/ftb_walkthrough_touch_circle.mp3', login));
      setFTBTutorial2(getVideo('appfiles/change_feelings/ftb_walkthrough_shrug.mp3', login));
      setFTBWalkthrough(getVideo('appfiles/ask/ftb_walkthrough_audio.mp3', login));
    }
  }, [login, module13.unlockedDays.day2]);

  useEffect(() => {
    loadGifAssets();
  }, [loadGifAssets]);

  // ------------------------------------------------------------------------
  // Animation Handlers
  // ------------------------------------------------------------------------

  const coinAnimation = () => {
    setCoinAnimationState('coinAnimation');
    setCoinAudio(true);
  };

  // ------------------------------------------------------------------------
  // Game State Management
  // ------------------------------------------------------------------------

  const createGameDetails = useCallback(
    (flag, reward, coin) => {
      const gameDetail = { ...module13.game_details };

      if (flag) {
        const flagEntry = { flag, lastUpdated: new Date().toJSON() };
        gameDetail.moduleFlags.push(flagEntry);
      }

      if (coin >= 0) {
        gameDetail.tokens_earned = coin;
      }

      if (reward) {
        const dailyRewards = Object.entries(module13.rewards)
          .filter(([, rewardData]) => rewardData.unlocked)
          .map(([key, rewardData]) => ({
            type: key,
            x_multiplier: rewardData.x,
            y_multiplier: rewardData.y,
          }));
        gameDetail.dailyRewards = dailyRewards;
      }

      dispatch({ type: 'MODULE13_SET_GameDetials', payload: gameDetail });
    },
    [dispatch, module13.game_details, module13.rewards]
  );

  // ------------------------------------------------------------------------
  // API Calls
  // ------------------------------------------------------------------------

  const updateProgress = async () => {
    const payload = {
      body: {
        id: login.studentid,
        module: 'ChangeFeelings',
        day_completed: module13.currentday,
        time_completed: new Date().toDateString(),
      },
    };

    try {
      await API.put('TIR', '/v3/student/progress', payload);
    } catch (error) {
      // Silent fail - progress update is non-critical
    }
  };

  const updateDB = async () => {
    const modulePayload = {
      body: {
        id: module13.moduleID,
        day_completed: module13.day_completed,
        step_completed: module13.stepCompleted,
        game_details: JSON.stringify(module13.game_details),
      },
    };

    try {
      await API.put('TIR', '/v3/student/module', modulePayload);

      if (displayGreenButton) {
        setAnimalVoice(null);
        setShowAnimal(true);
        setPlayLizardClose(true);
        setAnimalState(animalClose);
        setDisplayGreenButton(false);
        setDisplayReward(false);
      }
    } catch (error) {
      const criticalSteps = [STEPS.NOT_STARTED, STEPS.LEARN, STEPS.PRACTICE, STEPS.CELEBRATE, STEPS.FOLLOW_THE_BEAT];
      if (criticalSteps.includes(module13.stepCompleted)) {
        window.alert('Connection is lost. Please login again.');
        dispatch({ type: 'CLEAR' });
        Auth.signOut();
        navigate('/');
      }
    }

    // Update current module for student
    const studentPayload = {
      body: {
        id: login.studentid,
        current_module: 'ChangeFeelings',
      },
    };

    try {
      await API.put('TIR', '/v3/student', studentPayload);
    } catch (error) {
      // Silent fail - student update is non-critical
    }
  };

  // ------------------------------------------------------------------------
  // Effects for State Synchronization
  // ------------------------------------------------------------------------

  // Sync step completion to database
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (!isUnrestrictedGrade(login.grade)) {
      updateDB();
    }
  }, [module13.stepCompleted]);

  // Handle section completion
  useEffect(() => {
    const shouldUpdateProgress =
      (module13.reviewIcon || login.grade === 'Teacher Mode') &&
      module13.sectionCompleted === 4;

    if (shouldUpdateProgress) {
      updateProgress();
      module13.sectionCompleted = 0;
    }
  }, [module13.sectionCompleted]);

  // Handle coin updates and prize eligibility
  useEffect(() => {
    if (module13.coins >= COIN_PRIZE_THRESHOLD) {
      setPrize(true);
    }

    if (coinUpdate.current) {
      coinUpdate.current = false;
      return;
    }

    createGameDetails(false, false, module13.coins);
    updateDB();
  }, [module13.coins]);

  // ------------------------------------------------------------------------
  // UI Handlers
  // ------------------------------------------------------------------------

  const togglePopup = () => {
    setIsPopupOpen((prev) => !prev);
  };

  // Handle progress key updates based on step completion
  useEffect(() => {
    const isDayCompleted = module13.currentday <= module13.day_completed;
    const hasUnrestrictedAccess = isUnrestrictedGrade(login.grade);

    if (isDayCompleted || hasUnrestrictedAccess) {
      // Day is completed or user has unrestricted access
      if (hasUnrestrictedAccess) {
        setShowReward(true);
      }

      dispatch({ type: 'MODULE13_TOGGLE_REVIEW_ICON', payload: true });

      const isSpecialDay = module13.currentday === SPECIAL_DAYS.DAY_4 || module13.currentday === SPECIAL_DAYS.DAY_8;
      dispatch({ type: 'MODULE13_SET_KEY', payload: isSpecialDay ? 5 : 4 });
    } else {
      // Day is in progress
      if (module13.stepCompleted === checkStep) {
        setCoinIcon(true);
      }

      dispatch({ type: 'MODULE13_TOGGLE_REVIEW_ICON', payload: false });

      const isDay4Start = module13.currentday === SPECIAL_DAYS.DAY_4 && module13.day_completed === 3;

      if (isDay4Start) {
        // Day 4 has a different step sequence (includes singing time first)
        const day4KeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.SINGING_TIME]: 1,
          [STEPS.LEARN]: 2,
          [STEPS.PRACTICE]: 3,
          [STEPS.FOLLOW_THE_BEAT]: 4,
          [STEPS.CELEBRATE]: 5,
        };

        const keyValue = day4KeyMap[module13.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE13_SET_KEY', payload: keyValue });
          if (module13.stepCompleted === STEPS.CELEBRATE) {
            setKeyIcon(true);
          }
        }
      } else {
        // Standard day progression
        const standardKeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.LEARN]: 1,
          [STEPS.PRACTICE]: 2,
          [STEPS.FOLLOW_THE_BEAT]: 3,
          [STEPS.CELEBRATE]: 4,
          [STEPS.SURPRISE]: 5,
        };

        const keyValue = standardKeyMap[module13.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE13_SET_KEY', payload: keyValue });

          // Handle special cases for celebrate step
          if (module13.stepCompleted === STEPS.CELEBRATE) {
            const isDay8Surprise = module13.currentday === SPECIAL_DAYS.DAY_8 && module13.day_completed === 7;
            if (isDay8Surprise) {
              setPresentIcon(true);
            } else {
              setKeyIcon(true);
            }
          } else if (module13.stepCompleted === STEPS.SURPRISE) {
            setKeyIcon(true);
          }
        }
      }

      playButtonAudio();
    }
  }, [module13.stepCompleted]);

  // ------------------------------------------------------------------------
  // Reward Selection Handlers
  // ------------------------------------------------------------------------

  const handleRewardSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;
    const day = module13.currentday;

    // Update Redux state
    dispatch({ type: `MODULE13_SET_REWARD${day}1`, payload: isOption1 });
    dispatch({ type: `MODULE13_SET_REWARD${day}2`, payload: !isOption1 });

    // Update visual selection feedback
    document.getElementById('rewardItemLeft2').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight2').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handleRewardConfirmation = () => {
    document.getElementById('rewardList').style.zIndex = 1;
    setCheckSound(true);
    setDropZone(true);
    setDisplayGreenCheck(false);
    setDisplayGreenButton(true);
  };

  const handleDayCompletion = () => {
    setCheckSound(true);
    setDropZone(false);
    updateProgress();

    sleep(500).then(() => {
      createGameDetails(false, true);

      if (isUnrestrictedGrade(login.grade)) {
        updateDB();
      }

      const nextDay = module13.currentday + 1;
      const completedDay = module13.currentday === SPECIAL_DAYS.DAY_8 ? 9 : module13.currentday;

      dispatch({ type: `MODULE13_SET_DAY_UNLOCK${nextDay}`, payload: true });
      dispatch({ type: 'MODULE13_SET_dayCompleted', payload: completedDay });
      dispatch({ type: 'MODULE13_SET_stepCompleted', payload: STEPS.NOT_STARTED });
    });
  };

  const renderRewardSelection = () => (
    <>
      <div className="rewards" id="rewardList">
        <div className="circle" id="rewardItemLeft2">
          <img
            src={rewardOption1}
            alt="Reward option 1"
            onClick={() => handleRewardSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight2">
          <img
            src={rewardOption2}
            alt="Reward option 2"
            onClick={() => handleRewardSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={handleRewardConfirmation}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">Drag and drop your item</div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Complete day"
            onClick={handleDayCompletion}
          />
        )}
      </div>
    </>
  );

  // ------------------------------------------------------------------------
  // Prize Selection Handlers
  // ------------------------------------------------------------------------

  const handlePrizeSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;

    dispatch({ type: 'MODULE13_SET_PRIZE1', payload: isOption1 });
    dispatch({ type: 'MODULE13_SET_PRIZE2', payload: !isOption1 });

    document.getElementById('rewardItemLeft').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handlePrizeConfirmation = () => {
    setDisplayPrize(false);
    setCheckSound(true);
    setDropZone(false);

    sleep(500).then(() => {
      setDisplayGreenButton(false);
      createGameDetails(false, true);
      dispatch({ type: 'MODULE13_SET_COIN', payload: module13.coins - COIN_PRIZE_THRESHOLD });
    });
  };

  const renderPrizeSelection = () => (
    <>
      <div className="rewards">
        <div className="circle" id="rewardItemLeft">
          <img
            src={images.rewards.Volcano}
            alt="Volcano prize"
            onClick={() => handlePrizeSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight">
          <img
            src={images.rewards.TRex}
            alt="T-Rex prize"
            onClick={() => handlePrizeSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={() => {
              setCheckSound(true);
              setDropZone(true);
              setDisplayGreenCheck(false);
              setDisplayGreenButton(true);
            }}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">Drag and drop your item</div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm prize"
            onClick={handlePrizeConfirmation}
          />
        )}
      </div>
    </>
  );

  // Set reward options based on current day
  useEffect(() => {
    const options = rewardOptionsByDay[module13.currentday];

    if (options) {
      setRewardOption1(options[0]);
      setRewardOption2(options[1]);
    } else {
      setDisplayGreenButton(true);
    }
  }, [keyIcon, module13.currentday]);

  // ------------------------------------------------------------------------
  // Review Mode Rendering
  // ------------------------------------------------------------------------

  const renderReviewButtons = () => {
    const isDay4 = module13.currentday === SPECIAL_DAYS.DAY_4;

    if (isDay4) {
      // Day 4 includes singing time button
      return (
        <div className="world2reviewContainer">
          <img
            className="reviewBtns41"
            src={images.singingTimeBtn}
            alt="singing button"
            onClick={displaySinging}
          />
          <img
            className="reviewBtns42"
            src={images.learnBtn}
            alt="learn button"
            onClick={openLearn}
          />
          <img
            className="reviewBtns43"
            src={images.practiceBtn}
            alt="practice button"
            onClick={openPractice}
          />
          <img
            className="reviewBtns44"
            src={images.ftbBtn}
            alt="ftb button"
            onClick={openFTB}
          />
          <img
            className="reviewBtns45"
            src={images.celebrateBtn}
            alt="celebrate button"
            onClick={openCelebrate}
          />
        </div>
      );
    }

    // Standard review buttons
    return (
      <div className="world2reviewContainer">
        <img
          className="reviewBtns1"
          src={images.learnBtn}
          alt="learn button"
          onClick={openLearn}
        />
        <img
          className="reviewBtns2"
          src={images.practiceBtn}
          alt="practice button"
          onClick={openPractice}
        />
        <img
          className="reviewBtns3"
          src={images.ftbBtn}
          alt="ftb button"
          onClick={openFTB}
        />
        <img
          className="reviewBtns4"
          src={images.celebrateBtn}
          alt="celebrate button"
          onClick={openCelebrate}
        />
      </div>
    );
  };

  // ------------------------------------------------------------------------
  // Video and Activity Handlers
  // ------------------------------------------------------------------------

  const handleIntroVideoEnd = () => {
    setDisplayIntroVideo(false);
    setDisplayBody(true);

    if (module13.currentday === SPECIAL_DAYS.DAY_4) {
      dispatch({ type: 'MODULE13_SET_KEY', payload: 1 });
      playButtonAudio();
    }

    if (module13.day_completed === 9) {
      navigate(NAVIGATION_PATH);
    }
  };

  const renderIntroduction = () => (
    <div>
      <CloudfrontVideoFormat
        source={introVideo}
        back={NAVIGATION_PATH}
        background={images.background}
        token={login.cookie}
        ended={handleIntroVideoEnd}
      />
    </div>
  );

  // Learn video configuration by day
  const learnVideosByDay = {
    1: { video: 'appfiles/change_feelings/learn_day_1.mp4', closingVideo: 'appfiles/change_feelings/learn_day_1_closing.mp4' },
    2: { video: 'appfiles/change_feelings/learn_day_2.mp4', closingVideo: 'appfiles/change_feelings/learn_day_2_closing.mp4' },
    3: { video: 'appfiles/change_feelings/learn_day_3.mp4', closingVideo: 'appfiles/change_feelings/learn_day_3_closing.mp4' },
  };

  const openLearn = async () => {
    const song = getVideo('appfiles/change_feelings/change_feelings_demo.m4a', login);
    const video = getVideo('appfiles/change_feelings/learn_video.mp4', login);
    const dayConfig = learnVideosByDay[module13.currentday];

    if (dayConfig) {
      // Days 1-3: Use pre-configured videos
      setLearnComponent(
        <Learn
          video={getVideo(dayConfig.video, login)}
          songVideo={video}
          closingVideo={getVideo(dayConfig.closingVideo, login)}
          destiny="/changefeelings"
          song={song}
          back="/changefeelings"
          background={images.background}
          setDisplayLearn={setDisplayLearn}
          step_completed="MODULE13_SET_stepCompleted"
          module={module13}
        />
      );
    } else {
      // Days 4+: Load user-generated content
      setAnimalVoice(getVideo('appfiles/change_feelings/change_feelings_learn_intro.mp3', login));
      setAnimalState(learnIntro);
      setShowAnimal(true);

      try {
        const userSongUrl = await GetUserVideo(`userfiles/${module13.StudentID}/change_feelings_play_day_music.m4a`);
        const response = await fetch(userSongUrl);
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module13.currentday - 4]}/ask_affirmation.mp4`
        );

        const hasUserSong = response.status === 200;

        setLearnComponent(
          <Learn
            songVideo={video}
            destiny="/changefeelings"
            song={hasUserSong ? userSongUrl : song}
            back="/changefeelings"
            background={images.background}
            setDisplayLearn={setDisplayLearn}
            step_completed="MODULE13_SET_stepCompleted"
            module={module13}
            instrumental={hasUserSong ? getVideo('appfiles/change_feelings/change_feelings_sing_along.m4a', login) : undefined}
            influencerVideo={influencerVideo}
          />
        );
      } catch (error) {
        console.error('Error loading learn content:', error);
      }
    }

    setDisplayLearn(true);
  };

  const openPractice = () => {
    setAnimalVoice(getVideo('appfiles/change_feelings/change_feelings_practice_intro.mp3', login));
    setAnimalState(practiceIntro);
    setShowAnimal(true);
    setDisplayPractice(true);
  };

  const openFTB = () => {
    // Days 4+ show intro animation
    if (module13.currentday > 3) {
      setAnimalVoice(getVideo('appfiles/change_feelings/change_feelings_ftb_intro.mp3', login));
      setAnimalState(ftbIntro);
      setShowAnimal(true);
    }

    setDisplayFTB(true);
  };

  // Celebrate video configuration by day
  const celebrateVideosByDay = {
    1: 'appfiles/change_feelings/celebrate_day_1.mp4',
    2: 'appfiles/change_feelings/celebrate_day_2.mp4',
    3: 'appfiles/change_feelings/celebrate_day_3.mp4',
  };

  const openCelebrate = async () => {
    const celebrateVideo = celebrateVideosByDay[module13.currentday];

    if (celebrateVideo) {
      // Days 1-3: Simple video celebration
      setCelebrateComponent(
        <Celebrate
          module={module13}
          celebrateVideo={getVideo(celebrateVideo, login)}
          background={images.background}
          setDisplayCelebrate={setDisplayCelebrate}
          moduleNum={MODULE_NAME}
        />
      );
    } else {
      // Days 4+: Interactive celebration with recording
      setAnimalVoice(getVideo('appfiles/change_feelings/change_feelings_celebrate_intro.mp3', login));
      setAnimalState(celebrateIntro);
      setShowAnimal(true);

      const voice1 = getVideo('appfiles/ask/ask_celebrate_record_important.mp3', login);
      const voice2 = getVideo('appfiles/ask/ask_celebrate_say_yes.mp3', login);
      const voice3 = getVideo('appfiles/ask/ask_awesome_job.mp3', login);
      const animalGIF = getVideo('appfiles/change_feelings/lizard joyful jingle.gif', login);
      const animalVoiceClip = getVideo('appfiles/change_feelings/change_feelings_joyful_jingle_intro.mp3', login);

      try {
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module13.currentday - 4]}/ask_affirmation.mp4`
        );

        setCelebrateComponent(
          <Celebrate
            world3={true}
            module={module13}
            background={images.background}
            voice1={voice1}
            voice2={voice2}
            voice3={voice3}
            animalGIF={animalGIF}
            animalVoice={animalVoiceClip}
            text='Record yourself in an excited voice and say, "I am a very important person!"'
            name={`change_feelings_celebration_surprise_${module13.currentday + 1}.mp4`}
            influencerVideo={influencerVideo}
            animation={amazingJob}
            animal={images.animal}
            setDisplayCelebrate={setDisplayCelebrate}
            moduleNum={MODULE_NAME}
            lizard={true}
            bigAnimal={true}
          />
        );
      } catch (error) {
        console.error('Error loading celebrate content:', error);
      }
    }

    setDisplayCelebrate(true);
  };

  const displaySinging = () => {
    const singingIntroVideo = getVideo('appfiles/change_feelings/singing_time.mp4', login);
    const noteVideo = getVideo('appfiles/change_feelings/change_feelings_student_notes.mp4', login);
    const singingMusic = getVideo('appfiles/change_feelings/change_feelings_demo.m4a', login);
    const instrumental = getVideo('appfiles/change_feelings/change_feelings_instrumental.m4a', login);

    setSingTimeComponent(
      <SingingTime
        introVideo={singingIntroVideo}
        demoMusic={singingMusic}
        singingVideo={noteVideo}
        background={images.background}
        singingMusic={singingMusic}
        setDisplaySingingTime={setDisplaySingingTime}
        module={module13}
        moduleNum={MODULE_NAME}
        name="change_feelings"
        instrumental={instrumental}
        instrumentalURL="appfiles/change_feelings/change_feelings_sing_along.m4a"
      />
    );

    setDisplaySingingTime(true);
  };

  // ------------------------------------------------------------------------
  // Surprise Video Handlers
  // ------------------------------------------------------------------------

  const getSurpriseSlides = async () => {
    const slidePaths = [
      { video: `influencers/${login.influencerid}/ask_affirmation.mp4` },
      { video: `userfiles/${module13.StudentID}/change_feelings_celebration_surprise_5.mp4` },
      { video: `influencers/${login.influencerid1}/ask_affirmation.mp4` },
      { video: `userfiles/${module13.StudentID}/change_feelings_celebration_surprise_7.mp4` },
      { video: `influencers/${login.influencerid2}/ask_video_message.mp4` },
      { video: `influencers/${login.influencerid3}/ask_affirmation.mp4` },
      { video: `userfiles/${module13.StudentID}/change_feelings_celebration_surprise_9.mp4` },
      { video: `influencers/${login.influencerid4}/ask_affirmation.mp4` },
    ];

    // Load all video URLs in parallel
    const loadedSlides = await Promise.all(
      slidePaths.map(async (slide) => ({
        video: await GetUserVideo(slide.video, login),
      }))
    );

    return loadedSlides;
  };

  const playSurpriseVideo = async () => {
    const slides = await getSurpriseSlides();

    setSurpriseVideo(
      <Surprise
        background={images.background}
        music={getVideo('appfiles/change_feelings/change_feelings_instrumental.m4a', login)}
        animal={images.animal}
        back="/"
        slides={slides}
        setDisplaySurprise={setDisplaySurprise}
      />
    );

    setDisplaySurprise(true);
    setKeyIcon(true);
    setPresentIcon(false);
  };

  // ------------------------------------------------------------------------
  // Animation Handlers
  // ------------------------------------------------------------------------

  const rewardAnimation = () => {
    setRewardAnimationState('fade-out-fly');
    sleep(1000).then(() => {
      flyAnimation();
    });
  };

  const flyAnimation = () => {
    setShowFly(true);
    setAnimalState(lizardTongue);
    setAnimalVoice(lizardTongueAudio);
    setShowAnimal(true);
    setKeyIcon(false);
  };

  const closeDay9Video = () => {
    setDisplayIntroVideo(true);
    setDisplayBody(false);
    setIntroVideo(getVideo(AFTER_CELEBRATE_VIDEO_DAY9, login));
    setShowAnimal(false);
  };

  // ------------------------------------------------------------------------
  // Drag & Drop Handler
  // ------------------------------------------------------------------------

  const handleDragStop = (rewardId, data) => {
    const position = {
      x: data.x / clientWidth,
      y: data.y / clientHeight,
    };

    const isPrize = rewardId === 1 || rewardId === 2;
    const actionType = isPrize
      ? `MODULE13_SET_PRIZE${rewardId}_POSITION`
      : `MODULE13_SET_REWARD${rewardId}_POSITION`;

    dispatch({ type: actionType, payload: position });
  };

  // ------------------------------------------------------------------------
  // iOS-Specific Timeout Effects
  // ------------------------------------------------------------------------

  // iOS fallback for voice audio
  useEffect(() => {
    if (!displayVoice || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setAudioReady(false);
      setDisplayVoice(false);
      setShowAnimal(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [displayVoice]);

  // iOS fallback for lizard close audio
  useEffect(() => {
    if (!playLizardClose || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setPlayLizardClose(false);
      if (module13.currentday === SPECIAL_DAYS.DAY_8) {
        closeDay9Video();
      } else {
        navigate(NAVIGATION_PATH);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [playLizardClose, module13.currentday, navigate]);

  // iOS fallback for coin audio
  useEffect(() => {
    if (!coinAudio || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      dispatch({ type: 'MODULE13_SET_COIN', payload: module13.coins + 1 });
      setCoinAudio(false);
      setCoinAnimationState('bounceIcons');
      setCoinIcon(false);
      playButtonAudio();
    }, 5000);

    return () => clearTimeout(timer);
  }, [coinAudio, dispatch, module13.coins, playButtonAudio]);

  // ------------------------------------------------------------------------
  // Audio Event Handlers
  // ------------------------------------------------------------------------

  const handleCoinAudioEnd = () => {
    dispatch({ type: 'MODULE13_SET_COIN', payload: module13.coins + 1 });
    setCoinAudio(false);
    setCoinAnimationState('bounceIcons');
    setCoinIcon(false);
    playButtonAudio();
  };

  const handleLizardCloseAudioEnd = () => {
    setPlayLizardClose(false);
    if (module13.currentday === SPECIAL_DAYS.DAY_8) {
      closeDay9Video();
    } else {
      navigate(NAVIGATION_PATH);
    }
  };

  const handleVoiceAudioEnd = () => {
    setAudioReady(false);
    setDisplayVoice(false);
    setShowAnimal(false);

    if (showFly) {
      setShowFly(false);
      setDisplayReward(true);
      setRewardsFound(true);
    }
  };

  const handleLogout = () => {
    navigate('/');
    userHasAuthenticated(false);
    localStorage.setItem('token', 'false');
  };

  // ------------------------------------------------------------------------
  // Main UI Conditions
  // ------------------------------------------------------------------------

  const shouldShowMainWorld =
    (displayBody &&
      !displayLearn &&
      !displayPractice &&
      !displaySingingTime &&
      !displayCelebrate &&
      !displaySurprise &&
      !displayFTB &&
      !displayDay4Practice) ||
    showAnimal;

  const isSpecialDay = module13.currentday === SPECIAL_DAYS.DAY_4 || module13.currentday === SPECIAL_DAYS.DAY_8;
  const progressBarSource = isSpecialDay ? progressBar5Keys[module13.keys] : progressBar4Keys[module13.keys];
  const progressBarMax = isSpecialDay ? 5 : 4;

  const showActionButtons =
    !showAnimal &&
    !module13.reviewIcon &&
    !coinIcon &&
    !displayPrize &&
    !dropZone;

  const coinTextClass =
    module13.coins < 10
      ? 'scoretext1'
      : module13.coins < COIN_PRIZE_THRESHOLD
      ? 'scoretext10'
      : 'scoretext100';

  // ------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------

  return (
    <>
      {/* ========================= Audio Elements ========================= */}
      {coinAudio && (
        <audio autoPlay onEnded={handleCoinAudioEnd}>
          <source src={generateImageUrl('media/Module5Videos/earn_token.mp3')} type="audio/mp3" />
        </audio>
      )}

      {playLizardClose && (
        <audio autoPlay onPlay={() => setAudioReady(true)} onEnded={handleLizardCloseAudioEnd}>
          <source src={animalCloseAudio} type="audio/mp3" />
        </audio>
      )}

      {checkSound && (
        <audio autoPlay onEnded={() => setCheckSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_accept_item.mp3')} type="audio/mp3" />
        </audio>
      )}

      {rewardsFound && (
        <audio autoPlay>
          <source src={generateImageUrl('media/Module2Videos/daily_rewards_present.mp3')} />
        </audio>
      )}

      {touchButton && (
        <audio autoPlay onEnded={() => setTouchButton(false)}>
          <source src={generateImageUrl('media/Module1Videos/touch_button.mp3')} type="audio/mp3" />
        </audio>
      )}

      {buttonSound && (
        <audio autoPlay onEnded={() => setButtonSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_bouncing.mp3')} type="audio/mp3" />
        </audio>
      )}

      {displayVoice && (
        <audio autoPlay onPlay={() => setAudioReady(true)} onEnded={handleVoiceAudioEnd}>
          <source src={animalVoice} />
        </audio>
      )}

      {/* ========================= Activity Screens ========================= */}
      {displayIntroVideo && renderIntroduction()}

      {!showAnimal && displayLearn && learnComponent}

      {!showAnimal && displayPractice && (
        <Practice
          setDisplayPractice={setDisplayPractice}
          background={images.background}
          stepComplete="MODULE13_SET_stepCompleted"
          module={module13}
          watchConversation={watchConversation}
          specialMessage={specialMessage}
          amazingJob={amazingJob}
          animal={images.animal}
        />
      )}

      {!showAnimal && displayFTB && (
        <FollowTheBeatModule8
          unit3={true}
          setDisplayFTB={setDisplayFTB}
          awesomeJob={awesomeJob}
          awesomeJobGIF={amazingJob}
          instruction={ftbInstruction}
          instructionGIF={ftbInstructionGIF}
          song={ftbMusic}
          count={ftbCount}
          tutorial1={ftbTutorial1}
          tutorial2={ftbTutorial2}
          walkthrough={ftbWalkthrough}
          stepComplete="MODULE13_SET_stepCompleted"
          module={module13}
          score="ftb_change_feelings"
        />
      )}

      {displaySingingTime && singTimeComponent}

      {!showAnimal && displayCelebrate && celebrateComponent}

      {displaySurprise && surpriseVideo}

      {/* ========================= Main World Content ========================= */}
      {shouldShowMainWorld && (
        <div className="askWorldBody" style={{ backgroundImage: `url(${images.background})` }}>
          {/* Navigation Bar */}
          <div className="navigation">
            <img
              className="logoutBtn"
              src={images.logoutBtn}
              alt="Logout Button"
              onClick={togglePopup}
            />
            <img
              className="JourneyBlueImage"
              src={images.journeyMap}
              alt="Navigation blue world"
              onClick={() => navigate(NAVIGATION_PATH)}
            />
          </div>

          {/* Logout Popup */}
          {isPopupOpen && (
            <PopUp
              content={
                <>
                  <b id="title">
                    Are you sure you want to leave?
                    <br />
                  </b>
                  <button id="logoutButton1" className="logoutButton" onClick={handleLogout}>
                    YES
                  </button>
                  <button id="logoutButton2" className="logoutButton" onClick={togglePopup}>
                    NO
                  </button>
                </>
              }
              handleClose={togglePopup}
            />
          )}

          {/* Prize Notification */}
          {prize && (
            <>
              <audio autoPlay>
                <source src={presentSound} />
              </audio>
              <img
                className="prize"
                src={generateImageUrl('images/Images/prize.png')}
                alt="Prize"
                onClick={() => {
                  setPrize(false);
                  rewardAnimation();
                  sleep(6000).then(() => setDisplayPrize(true));
                }}
              />
              <button
                className="prize-button"
                onClick={() => {
                  setPrize(false);
                  setDisplayPrize(true);
                }}
              >
                <h5>Click Here</h5>
              </button>
            </>
          )}

          {/* Prize Selection */}
          {displayPrize && renderPrizeSelection()}

          {/* Lizard Character */}
          <img className="lizard" src={images.animal} alt="lizard Icon" />

          {/* Loading indicator for animal animation */}
          {showAnimal && !audioReady && <img alt="Loading" id="loading" src={loadingGif} />}

          {/* Animated Lizard */}
          {showAnimal && (
            <img
              className={animalState === lizardTongue ? 'lizardTongue' : 'animatedLizard'}
              style={audioReady ? {} : { display: 'none' }}
              src={animalState}
              onLoad={() => setDisplayVoice(true)}
              onError={() => setShowAnimal(false)}
              alt="Animated lizard"
            />
          )}

          {/* Score Bar */}
          <div className="scoreBar">
            <img className="scoreKey" src={images.coinBar} alt="coin bar" />
            <h5 className={coinTextClass}>{module13.coins}</h5>
            <p className="scoretext">100</p>
          </div>

          {/* Progress Bar */}
          <div className="progressBar3">
            <img className="progressKey3" src={progressBarSource} alt="progress bar" />
            <h3 className="progresstext3">
              {module13.keys}/{progressBarMax}
            </h3>
          </div>

          {/* Action Buttons (Main Progression) */}
          {showActionButtons && (
            <div className="actionBtnDiv">
              {/* Day 4 starts with singing time */}
              {module13.currentday === SPECIAL_DAYS.DAY_4 && module13.stepCompleted === STEPS.NOT_STARTED && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.singingTimeBtn}
                  alt="singing time button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      displaySinging();
                      setCheckStep(STEPS.SINGING_TIME);
                    });
                  }}
                />
              )}

              {/* Other days start with learn */}
              {module13.currentday !== SPECIAL_DAYS.DAY_4 && module13.stepCompleted === STEPS.NOT_STARTED && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.learnBtn}
                  alt="learn button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openLearn();
                      setCheckStep(STEPS.LEARN);
                    });
                  }}
                />
              )}

              {/* Practice button after learn */}
              {module13.stepCompleted === STEPS.LEARN && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.practiceBtn}
                  alt="practice button"
                  onClick={() => {
                    touchSound.play();
                    sleep(500).then(() => {
                      openPractice();
                      setCheckStep(STEPS.PRACTICE);
                    });
                  }}
                />
              )}

              {/* FTB button after practice */}
              {module13.stepCompleted === STEPS.PRACTICE && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.ftbBtn}
                  alt="follow the beat button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openFTB();
                      setCheckStep(STEPS.FOLLOW_THE_BEAT);
                    });
                  }}
                />
              )}

              {/* Celebrate button after FTB */}
              {module13.stepCompleted === STEPS.FOLLOW_THE_BEAT && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.celebrateBtn}
                  alt="celebrate button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openCelebrate();
                      setCheckStep(STEPS.CELEBRATE);
                    });
                  }}
                />
              )}

              {/* Learn button after singing time (Day 4 only) */}
              {module13.stepCompleted === STEPS.SINGING_TIME && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.learnBtn}
                  alt="learn button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openLearn();
                      setCheckStep(STEPS.LEARN);
                    });
                  }}
                />
              )}
            </div>
          )}

          {/* Coin Collection */}
          {!displayVoice && coinIcon && !displayPrize && (
            <div className="actionBtnDiv">
              {!module13.reviewIcon && (
                <img
                  className="chooseReward"
                  id={coinAnimationState}
                  src={images.coinPick}
                  alt="reward coin"
                  style={{
                    position: 'absolute',
                    top: '50vh',
                    left: '50vw',
                    width: '100px',
                    height: '100px',
                    marginLeft: '-50px',
                    marginTop: '-50px',
                    zIndex: 10,
                  }}
                  onClick={coinAnimation}
                />
              )}
            </div>
          )}

          {/* Review and Reward Buttons */}
          {!displayPrize && !showAnimal && !displayReward && (
            <div className="actionBtnDiv">
              {module13.reviewIcon && (
                <img
                  className={showReward ? 'actionBtn3' : 'actionBtn2'}
                  src={images.reviewBtn}
                  alt="review button"
                  onClick={() => setReviewIconVisible((prev) => !prev)}
                />
              )}
              {showReward && (
                <img
                  src={images.rewardBtn}
                  className="actionBtn4"
                  alt="reward button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => rewardAnimation());
                  }}
                />
              )}
              {module13.reviewIcon && reviewIconVisible && renderReviewButtons()}
            </div>
          )}

          {/* Fly Reward Icon */}
          {!coinIcon && keyIcon && !displayPrize && (
            <img
              style={{ width: '80px', height: '75px' }}
              className="rewardsIcon"
              id={rewardAnimationState}
              src={images.fly}
              alt="reward fly"
              onClick={rewardAnimation}
            />
          )}

          {/* Animated Fly */}
          {showFly && audioReady && (
            <div id="flyY">
              <img
                style={{ width: '80px', height: '75px' }}
                className="flyPosition"
                id="flyX"
                alt="flying fly"
                src={images.fly}
              />
            </div>
          )}

          {/* Reward Selection */}
          {displayReward && renderRewardSelection()}

          {/* Present Icon (Day 8) */}
          {!displayPrize && !coinIcon && presentIcon && (
            <>
              <img
                className="present"
                style={{ width: '84px', height: '100px', marginLeft: '-42px', zIndex: 10 }}
                id="bounceIcons"
                src={images.present}
                alt="gift"
                onClick={playSurpriseVideo}
              />
              <audio autoPlay>
                <source src={presentSound} />
              </audio>
            </>
          )}

          {/* Draggable Rewards Area */}
          <div className="rewardArea">
            {module13.rewards.CactusBarell.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.CactusBarell.x * clientWidth,
                  y: module13.rewards.CactusBarell.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(11, data)}
              >
                <div className="moveSmallReward1" style={{ backgroundImage: `url(${images.rewards.CactusBarell})` }} />
              </Draggable>
            )}

            {module13.rewards.CactusPrickly.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.CactusPrickly.x * clientWidth,
                  y: module13.rewards.CactusPrickly.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(12, data)}
              >
                <div className="moveSmallReward2" style={{ backgroundImage: `url(${images.rewards.CactusPrickly})` }} />
              </Draggable>
            )}

            {module13.rewards.StegoBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.StegoBlue.x * clientWidth,
                  y: module13.rewards.StegoBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(21, data)}
              >
                <div className="moveStegoReward1" style={{ backgroundImage: `url(${images.rewards.StegoBlue})` }} />
              </Draggable>
            )}

            {module13.rewards.StegoGreen.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.StegoGreen.x * clientWidth,
                  y: module13.rewards.StegoGreen.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(22, data)}
              >
                <div className="moveStegoReward2" style={{ backgroundImage: `url(${images.rewards.StegoGreen})` }} />
              </Draggable>
            )}

            {module13.rewards.Tent.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Tent.x * clientWidth,
                  y: module13.rewards.Tent.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(31, data)}
              >
                <div className="moveTent1" style={{ backgroundImage: `url(${images.rewards.Tent})` }} />
              </Draggable>
            )}

            {module13.rewards.TentOld.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.TentOld.x * clientWidth,
                  y: module13.rewards.TentOld.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(32, data)}
              >
                <div className="moveBigReward2" style={{ backgroundImage: `url(${images.rewards.TentOld})` }} />
              </Draggable>
            )}

            {module13.rewards.LongNeckRed.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.LongNeckRed.x * clientWidth,
                  y: module13.rewards.LongNeckRed.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(41, data)}
              >
                <div className="moveLongBigReward1" style={{ backgroundImage: `url(${images.rewards.LongNeckRed})` }} />
              </Draggable>
            )}

            {module13.rewards.LongNeckGreen.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.LongNeckGreen.x * clientWidth,
                  y: module13.rewards.LongNeckGreen.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(42, data)}
              >
                <div className="moveLongBigReward2" style={{ backgroundImage: `url(${images.rewards.LongNeckGreen})` }} />
              </Draggable>
            )}

            {module13.rewards.Canteen.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Canteen.x * clientWidth,
                  y: module13.rewards.Canteen.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(51, data)}
              >
                <div className="moveCanteenReward1" style={{ backgroundImage: `url(${images.rewards.Canteen})` }} />
              </Draggable>
            )}

            {module13.rewards.Backpack.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Backpack.x * clientWidth,
                  y: module13.rewards.Backpack.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(52, data)}
              >
                <div className="moveBackpack2" style={{ backgroundImage: `url(${images.rewards.Backpack})` }} />
              </Draggable>
            )}

            {module13.rewards.Plant1.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Plant1.x * clientWidth,
                  y: module13.rewards.Plant1.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(61, data)}
              >
                <div className="movePlantReward1" style={{ backgroundImage: `url(${images.rewards.Plant1})` }} />
              </Draggable>
            )}

            {module13.rewards.Plant2.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Plant2.x * clientWidth,
                  y: module13.rewards.Plant2.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(62, data)}
              >
                <div className="movePlantReward2" style={{ backgroundImage: `url(${images.rewards.Plant2})` }} />
              </Draggable>
            )}

            {module13.rewards.Bandanna.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Bandanna.x * clientWidth,
                  y: module13.rewards.Bandanna.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(71, data)}
              >
                <div className="moveCharaterReward1" style={{ backgroundImage: `url(${images.rewards.Bandanna})` }} />
              </Draggable>
            )}

            {module13.rewards.Hat.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Hat.x * clientWidth,
                  y: module13.rewards.Hat.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(72, data)}
              >
                <div className="moveCharaterReward2" style={{ backgroundImage: `url(${images.rewards.Hat})` }} />
              </Draggable>
            )}

            {module13.rewards.RaptorDark.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.RaptorDark.x * clientWidth,
                  y: module13.rewards.RaptorDark.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(81, data)}
              >
                <div className="moveRaptorReward1" style={{ backgroundImage: `url(${images.rewards.RaptorDark})` }} />
              </Draggable>
            )}

            {module13.rewards.RaptorLight.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.RaptorLight.x * clientWidth,
                  y: module13.rewards.RaptorLight.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(82, data)}
              >
                <div className="moveRaptorReward2" style={{ backgroundImage: `url(${images.rewards.RaptorLight})` }} />
              </Draggable>
            )}

            {module13.rewards.Volcano.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.Volcano.x * clientWidth,
                  y: module13.rewards.Volcano.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(1, data)}
              >
                <div className="movePrize13" style={{ backgroundImage: `url(${images.rewards.Volcano})` }} />
              </Draggable>
            )}

            {module13.rewards.TRex.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module13.rewards.TRex.x * clientWidth,
                  y: module13.rewards.TRex.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(2, data)}
              >
                <div className="movePrize23" style={{ backgroundImage: `url(${images.rewards.TRex})` }} />
              </Draggable>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Module13World;
